import React, {useCallback, useEffect, useMemo, useState} from "react";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import {Button, ButtonGroup, Col, Container, Input, Nav, NavItem, NavLink, Row, TabContent, TabPane} from "reactstrap";
import usePermissions from "../../../hooks/usePermissions";
import classnames from "classnames";
import {toast} from "react-toastify";
import TabPaneContent from "./components/TabPaneContent";
import {ENABLED_ACTIONS_BY_TYPE, INITIAL_MODAL_STATE, modalKeys, MODALS, ModalState} from "./constants/modals";
import {useDispatch, useSelector} from "react-redux";
import {useFilter} from "../../../hooks/useFilter";
import {getUpdateBrokerName} from "../../../store/tracker/actions";
import useDebounce from "../../../hooks/useDebounce";
import FilterOrder from "./components/filters/FilterOrder";
import {Navigate, useParams} from "react-router-dom";
import {IExtendedOrderFilters} from "./components/filters/types";
import {useCrmSystems} from "../../../hooks/useCrmSystems";
import {OrderQueryType, useOrdersQuery} from "./constants/query";

const TABS = [
    {id: "1", label: "Active", type: "active" as OrderQueryType},
    {id: "2", label: "Waiting", type: "waiting" as OrderQueryType},
    {id: "3", label: "Not Active", type: "notActive" as OrderQueryType},
];

const TrafficOrders: React.FC = () => {
    useEffect(() => {
        document.title = "Traffic Orders | Dashboard";
    }, []);

    const {crmId: crmIdParam} = useParams<{ crmId: string }>();
    const crmId = Number(crmIdParam);

    const {hasPermission} = usePermissions();
    const canView = hasPermission("view-traffic-orders") && Number.isFinite(crmId);
    const canViewNotActive = hasPermission('view-traffic-orders-not-active');

    const {data: crmSystems, isError: isErrorCrm} = useCrmSystems(Number.isFinite(crmId));

    const {filter, setFilter} = useFilter();

    const dispatch = useDispatch();
    const {loadingUpdateLoadingName} = useSelector((state: any) => state.traker);

    const [activeTab, setActiveTab] = useState("1");
    const [modalState, setModalState] = useState<ModalState>(INITIAL_MODAL_STATE);

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const [extendedFilters, setExtendedFilters] = useState<IExtendedOrderFilters>({})
    useEffect(() => {
        setExtendedFilters({});
    }, [crmId]);

    useEffect(() => {
        if (!filter.q && !debouncedSearchTerm) return;
        setFilter({...filter, q: debouncedSearchTerm.toLowerCase()});
    }, [debouncedSearchTerm]);

    const handlerUpdateBrokName = () => dispatch(getUpdateBrokerName());

    const toggleModal = (key: keyof ModalState, id?: string | null, date?: string | null, tabId?: string | null) => {
        setModalState((prevState) => ({
            ...prevState,
            [key]: !prevState[key],
            selectedOrderId: id || null,
            selectedDate: date || null,
            tabId: tabId || null,
        }));
    };

    const queryParams = useMemo(
        () => ({...filter, ...extendedFilters, page: undefined}),
        [filter, extendedFilters],
    );

    const active = useOrdersQuery(crmId, "active", queryParams, canView);
    const waiting = useOrdersQuery(crmId, "waiting", queryParams, canView);
    const notActive = useOrdersQuery(crmId, "notActive", queryParams, canView && canViewNotActive);

    const queries = {active, waiting, notActive};

    const handleToggleModal = useCallback((key: string, orderId?: string | null, date?: string | null, tabId?: string) => {
        if (modalKeys.includes(key as keyof ModalState)) {
            toggleModal(key as keyof ModalState, orderId, date, tabId);
        }
    }, []);

    if (!canView || isErrorCrm) return <Navigate to="/dashboard"/>

    const visibleTabs = TABS.filter(t => t.type !== "notActive" || canViewNotActive);

    const crmTitle = crmSystems?.data.find(cs => cs.id === crmId)?.name;
    const breadcrumbCrmTitle = crmTitle ? ` | ${crmTitle}` : "";

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Dashboard" breadcrumbItem={"Traffic Orders" + breadcrumbCrmTitle}/>
                <Row className="align-items-center">
                    <Col md={12} className="d-sm-flex justify-content-between">
                        <div className="align-items-center justify-content-start gap-2 mb-3">
                            {hasPermission('traffic-orders-close-all-toast') &&
                                <Button onClick={() => toast.dismiss()} className="btn btn-light"
                                        disabled={!hasPermission('traffic-orders-close-all-toast')}>
                                    Read all notify
                                </Button>
                            }
                        </div>
                        <div className="d-flex align-items-center justify-content-end gap-2 mb-3">
                            <div className="d-flex align-items-center">
                                <Input
                                    placeholder="Search..."
                                    type="search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <ButtonGroup>
                                {hasPermission('update-brok-name') &&
                                    <Button onClick={handlerUpdateBrokName} className="btn btn-light"
                                            disabled={loadingUpdateLoadingName}>
                                        {loadingUpdateLoadingName &&
                                            <i className="bx bx-hourglass bx-spin font-size-16 align-middle me-2"></i>}
                                        Refresh Broker
                                    </Button>
                                }

                                {hasPermission('view-traffic-rules') &&
                                    <Button onClick={() => toggleModal("isTrafficRules")}
                                            className="btn btn-light d-flex align-items-center gap-1"
                                            disabled={!hasPermission('view-traffic-rules')}>
                                        <i className="bx bx-list-check"></i> Traffic Rules
                                    </Button>
                                }

                                {hasPermission('add-traffic-orders') &&
                                    <Button onClick={() => toggleModal("isRight")}
                                            className="btn btn-light d-flex align-items-center gap-1"
                                            disabled={!hasPermission('add-traffic-orders')}>
                                        <i className="bx bx-plus"></i> Add Order
                                    </Button>
                                }
                            </ButtonGroup>
                        </div>
                    </Col>
                </Row>

                <FilterOrder crmId={crmId} extendedFilters={extendedFilters}
                             onExtendedFiltersChange={setExtendedFilters}/>

                <Row>
                    <Col lg="12">
                        <Nav pills className="navtab-bg nav-justified mb-4">
                            {visibleTabs.map(({id, label}) => (
                                <NavItem key={id}>
                                    <NavLink
                                        style={{cursor: "pointer"}}
                                        className={classnames({active: activeTab === id})}
                                        onClick={() => setActiveTab(id)}
                                    >
                                        {label}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>

                        <TabContent activeTab={activeTab} className="p-3 text-muted">
                            {visibleTabs.map(({id, type}) => {
                                const q = queries[type];
                                return (
                                    <TabPane tabId={id} key={id}>
                                        <TabPaneContent
                                            activeTabId={id}
                                            isLoading={q.isLoading}
                                            isFetchingNext={q.isFetchingNextPage}
                                            isError={q.isError}
                                            isSuccess={q.isSuccess}
                                            hasNextPage={q.hasNextPage}
                                            trafficOrders={q.data?.trafficOrders}
                                            onLoadMore={q.fetchNextPage}
                                            toggleModal={handleToggleModal}
                                            modalActions={ENABLED_ACTIONS_BY_TYPE[type]}
                                        />
                                    </TabPane>
                                );
                            })}
                        </TabContent>
                    </Col>
                </Row>

                {MODALS.map(({key, Component}) =>
                    modalState[key as keyof ModalState] ? (
                        <Component
                            key={key}
                            isOpen={!!modalState[key as keyof ModalState]}
                            toggle={() => toggleModal(key as keyof ModalState)}
                            id={modalState.selectedOrderId || ""}
                            date={modalState.selectedDate || ""}
                            tabId={modalState.tabId || ""}
                            crmId={crmId}
                        />
                    ) : null
                )}
            </Container>
        </div>
    );
};

export default TrafficOrders;